import { useAtom } from '@reatom/npm-react'
import React, { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import styled, { css } from 'styled-components'

import {
  CurrencyIcon,
  CurrencyInformationSocial,
} from '@root/entities/currency/ui'
import { PairBoostersBadge } from '@root/entities/pair/ui'
import { PairExploreItemMetrics } from '@root/features/explore/ui/pair-explore-item/metrics/pair-explore-item-metrics'
import { PairExploreItemAudit } from '@root/features/explore/ui/pair-explore-item/pair-explore-item-audit'
import { TradeRoute } from '@root/features/routing'
import { NumberUtil } from '@root/shared'
import { Separator } from '@root/shared/ui-kit'
import { CreatedBy } from '@root/shared/ui-kit/created-by'

import {
  BuyButton,
  Chart,
  ChartBuyButton as ChartBuyButton_,
  CurrencyCardRoot,
  CurrencyCardTop,
  CurrencyCardTopInfo,
  DetailedCurrencyInfo,
  DetailedCurrencyInfoSide,
  Name,
  NameWithBoosters,
  Ticker,
  TickerValue,
} from './currency-card'
import { CurrencyCardEmptyChart } from './currency-card-empty-chart'
import { MarketCap } from './market-cap'

import type { PairModel } from '@root/entities/pair/atoms/reatom-pair'
import type { ClassNameProps } from '@root/shared/react'
import type { UTCTimestamp } from 'lightweight-charts'

const radius = () => 10

type Props = {
  pair: PairModel
  onClick?: () => void
} & ClassNameProps

export const CurrencyCardCompact: React.FC<Props> = memo((props) => {
  const { pair, className, onClick } = props
  const { t } = useTranslation()
  const [priceChart] = useAtom(pair.priceChartMinutes)
  const [premium] = useAtom(pair.premiumInfo.premium)
  const [socials] = useAtom(pair.quote.socials)
  const [marketCapUsd] = useAtom(pair.marketCapUsd)
  const [icon] = useAtom(pair.quote.icon)

  const candles = useMemo(
    () =>
      priceChart.map((x) => ({
        time: new Date(x.timestamp).getTime() as UTCTimestamp,
        open: NumberUtil.safeParseFloat(x.price.open) ?? 0,
        high: NumberUtil.safeParseFloat(x.price.high) ?? 0,
        low: NumberUtil.safeParseFloat(x.price.low) ?? 0,
        close: NumberUtil.safeParseFloat(x.price.close) ?? 0,
      })),
    [priceChart],
  )

  return (
    <CurrencyCardRoot
      as={onClick ? undefined : Link}
      onClick={onClick}
      to={TradeRoute.build({
        pairAddress: pair.address,
        network: pair.network,
      })}
      className={className}
    >
      <CurrencyCardTop>
        <CurrencyIcon
          currency={pair.quote}
          size={70}
          outline={premium ? 'premium' : 'outline'}
        />
        <CurrencyCardTopInfo>
          <NameWithBoosters>
            <Name ellipsis $premium={premium}>
              {pair.quote.title}
            </Name>
            <PairBoostersBadge
              currency={pair.quote}
              premiumInfo={pair.premiumInfo}
              mode="explore"
            />
          </NameWithBoosters>
          <DetailedCurrencyInfo>
            <DetailedCurrencyInfoSide>
              <Ticker>
                <span>{t('fun.ticker')}:</span>
                <TickerValue>{pair.quote.ticker}</TickerValue>
              </Ticker>
              <PairExploreItemAudit showProvider pair={pair} size={12} />
              <PairExploreItemMetrics
                statsPeriod="5M"
                pair={pair}
                showVolume={false}
              />
            </DetailedCurrencyInfoSide>
            <DetailedCurrencyInfoSide>
              <ChartBuyButton>
                {candles.length > 0 ? (
                  <Chart
                    candles={candles}
                    radius={radius}
                    css={css`
                      z-index: 0;
                    `}
                  />
                ) : (
                  <CurrencyCardEmptyChart />
                )}
                <BuyButton pair={pair} section="exploreFeeds" />
              </ChartBuyButton>
              <RightColumnFoorter>
                <MarketCap value={marketCapUsd} />
              </RightColumnFoorter>
            </DetailedCurrencyInfoSide>
          </DetailedCurrencyInfo>
        </CurrencyCardTopInfo>
      </CurrencyCardTop>
      <Separator opacityMaxPercent={10} />
      <Footer>
        <Socials>
          <CurrencyInformationSocial socials={socials} />
        </Socials>

        <CreatedBy address={pair.quote.address} avatar={icon} />
      </Footer>
    </CurrencyCardRoot>
  )
})

export const Footer = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: space-between;
`

export const Socials = styled.div`
  min-width: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const RightColumnFoorter = styled.div``

const ChartBuyButton = styled(ChartBuyButton_)`
  grid-row: span 2;
`
